import axios from 'axios'
import * as Sentry from '@sentry/react'
import { API_CONSUMER, API_VERSION, AUTH_SCHEME, HTTP_HEADERS } from '../constants/api.js'

const API_VERSION_SUFFIX = new RegExp(`/(?:${Object.values(API_VERSION).join('|')})/?$`)
const withoutApiVersionSuffix = (baseURL) => baseURL?.replace(API_VERSION_SUFFIX, '')

/**
 * Instancia de Axios autenticada para el servicio de informes
 */
export const authenticatedAxios = axios.create({
  baseURL: withoutApiVersionSuffix(import.meta.env.VITE_INFORMES_SERVICE_URL),
  timeout: 30000,
  withCredentials: true,
})

/**
 * Instancia de Axios autenticada para el gateway de reporting
 */
export const reportingAxios = axios.create({
  baseURL: import.meta.env.VITE_API_GATEWAY_REPORTING,
  timeout: 30000,
  withCredentials: true,
})

/**
 * Instancia de Axios autenticada para el gateway de company (core)
 */
export const coreCompanyAxios = axios.create({
  baseURL: withoutApiVersionSuffix(import.meta.env.VITE_API_GATEWAY_CORE_COMPANY),
  timeout: 30000,
  withCredentials: true,
})

/**
 * Configuración de interceptores para autenticación con tokens de Keycloak
 * Debe llamarse una vez que el AuthContext esté disponible
 */
export const setupAxiosInterceptors = (getAccessToken, refreshToken, logout) => {
  // Lista de instancias que requieren autenticación
  const authenticatedInstances = [authenticatedAxios, reportingAxios, coreCompanyAxios]
  const interceptorIds = []

  authenticatedInstances.forEach((axiosInstance) => {
    /**
     * Request Interceptor - Agrega el token a todas las peticiones
     */
    const requestId = axiosInstance.interceptors.request.use(
      async (config) => {
        const token = getAccessToken()

        if (token) {
          config.headers[HTTP_HEADERS.AUTHORIZATION] = `${AUTH_SCHEME} ${token}`
        }
        config.headers[HTTP_HEADERS.CONSUMER] = API_CONSUMER

        Sentry.addBreadcrumb({
          category: 'api',
          message: `API Request: ${config.method?.toUpperCase()} ${config.url}`,
          level: 'info',
          data: {
            method: config.method,
            url: config.url,
            baseURL: config.baseURL,
          },
        })

        return config
      },
      (error) => {
        Sentry.captureException(error, {
          tags: { feature: 'api', phase: 'request' },
        })
        throw error
      }
    )

    /**
     * Response Interceptor - Maneja errores 401 y refresca el token
     */
    const responseId = axiosInstance.interceptors.response.use(
      (response) => {
        Sentry.addBreadcrumb({
          category: 'api',
          message: `API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
          level: 'info',
          data: {
            status: response.status,
            url: response.config.url,
          },
        })

        return response
      },
      async (error) => {
        const originalRequest = error.config

        // Si es un error 401 y no se ha reintentado aún
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Token expirado, intentando refrescar',
              level: 'warning',
            })

            // Intentar refrescar el token
            const newTokens = await refreshToken()

            if (newTokens?.access_token) {
              // Actualizar el token en la petición original y reintentar
              originalRequest.headers = originalRequest.headers ?? {}
              originalRequest.headers[HTTP_HEADERS.AUTHORIZATION] = `${AUTH_SCHEME} ${newTokens.access_token}`
              originalRequest.headers[HTTP_HEADERS.CONSUMER] = API_CONSUMER
              return axiosInstance(originalRequest)
            }

            throw error
          } catch (refreshError) {
            Sentry.captureException(refreshError, {
              tags: { feature: 'auth', action: 'refresh-on-401' },
            })

            if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
              logout()
            }

            throw refreshError
          }
        }

        // Capturar error en Sentry
        Sentry.captureException(error, {
          tags: {
            feature: 'api',
            phase: 'response',
            status: error.response?.status,
          },
          contexts: {
            api: {
              url: error.config?.url,
              method: error.config?.method,
              baseURL: error.config?.baseURL,
              status: error.response?.status,
              message: error.response?.data?.message || error.message,
            },
          },
        })

        throw error
      }
    )

    interceptorIds.push({ axiosInstance, requestId, responseId })
  })

  // Retorna función de limpieza para evitar acumulación de interceptores
  return () => {
    interceptorIds.forEach(({ axiosInstance, requestId, responseId }) => {
      axiosInstance.interceptors.request.eject(requestId)
      axiosInstance.interceptors.response.eject(responseId)
    })
  }
}
